import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskedLine } from "./motion/Reveal";
import { TEST_IDS } from "../constants/testIds";
import { scrollTo } from "../lib/smoothScroll";

const HERO_IMG =
  "https://images.pexels.com/photos/32032996/pexels-photo-32032996.jpeg?auto=compress&cs=tinysrgb&w=1920";

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 240]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid={TEST_IDS.hero.section}
      className="relative w-full h-screen min-h-[720px] overflow-hidden bg-[#0A0C10]"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={HERO_IMG}
          alt="Impianto termico Foxtherm"
          className="w-full h-full object-cover"
          style={{ filter: "grayscale(0.6) contrast(1.05) brightness(0.55)" }}
        />
      </motion.div>

      {/* Vignette + tech grid overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0C10]/70 via-[#0A0C10]/30 to-[#0A0C10]" />
      <div className="absolute inset-0 bg-tech-grid opacity-30 mix-blend-overlay" />

      {/* Top meta line */}
      <motion.div
        style={{ opacity }}
        className="absolute top-24 md:top-28 inset-x-0 z-10"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-center justify-between text-white/60">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="font-mono text-[10px] tracking-[0.3em] uppercase flex items-center gap-3"
          >
            <span className="inline-block w-2 h-2 bg-[#E31E24] animate-pulse" />
            25+ anni
          </motion.div>
        </div>
      </motion.div>

      {/* Main kinetic title */}
      <div className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-24">
        <div className="max-w-[1600px] w-full mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="eyebrow mb-6"
          >
            <span className="inline-block mr-3 w-8 h-px bg-[#E31E24] align-middle" />
            Impianti: Idrosanitari / Termici / HVAC
          </motion.div>

          <h1
            data-testid={TEST_IDS.hero.title}
            className="font-display font-black uppercase text-white leading-[0.88] tracking-tighter text-[16vw] md:text-[13vw] lg:text-[11.5vw] xl:text-[10.5vw]"
          >
            <MaskedLine delay={0.35}>Il calore</MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="italic font-editorial font-light text-white/90">che</span>{" "}
              <span className="text-[#E31E24]">abita</span>
            </MaskedLine>
            <MaskedLine delay={0.65}>lo spazio.</MaskedLine>
          </h1>

          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <motion.p
              data-testid={TEST_IDS.hero.subtitle}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.9 }}
              className="md:col-span-6 max-w-xl text-white/70 text-base md:text-lg leading-relaxed"
            >
              Foxtherm realizza impianti idrosanitari, sistemi HVAC, impianti
              termici con specializzazione in sistemi radianti.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.9 }}
              className="md:col-span-6 flex flex-wrap items-center gap-4 md:justify-end"
            >
              <a
                href="#impianti"
                data-testid={TEST_IDS.hero.ctaPrimary}
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo("#impianti");
                }}
                className="btn-heat"
              >
                Scopri gli impianti
                <span aria-hidden>→</span>
              </a>
              <a
                href="#contatti"
                data-testid={TEST_IDS.hero.ctaSecondary}
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo("#contatti");
                }}
                className="btn-ghost"
              >
                Richiedi preventivo
              </a>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <motion.div
        data-testid={TEST_IDS.hero.scrollHint}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 right-6 md:right-10 z-10 hidden md:flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] text-white/50 uppercase">
          Scroll
        </span>
        <span className="w-px h-14 bg-white/30 relative overflow-hidden">
          <span className="absolute top-0 left-0 w-full h-4 bg-[#E31E24] animate-[scrollhint_1.6s_linear_infinite]" />
        </span>
      </motion.div>

      <style>{`
        @keyframes scrollhint {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(400%); }
        }
      `}</style>
    </section>
  );
}
